import React, { useState, useEffect, useCallback } from 'react';
import './NewsletterDashboard.css';

const auth = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function NewsletterDashboard() {
  const [view, setView]           = useState('overview'); // overview | compose | campaign
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats]         = useState({ total: 0, active: 0 });
  const [loading, setLoading]     = useState(true);
  const [editCamp, setEditCamp]   = useState(null); // null = new
  const [form, setForm]           = useState({ subject: '', body_html: '' });
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');
  const [sendConfirm, setSendConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, cr] = await Promise.all([
        fetch('/api/newsletter/subscribers', { headers: auth() }),
        fetch('/api/newsletter/campaigns',   { headers: auth() }),
      ]);
      const [sd, cd] = await Promise.all([sr.json(), cr.json()]);
      if (sd.success) { setSubscribers(sd.data); setStats(sd.stats); }
      if (cd.success) setCampaigns(cd.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditCamp(null); setForm({ subject: '', body_html: '' }); setMsg(''); setView('compose'); };

  const openEdit = async (id) => {
    const r = await fetch(`/api/newsletter/campaigns/${id}`, { headers: auth() });
    const d = await r.json();
    if (d.success) { setEditCamp(d.data); setForm({ subject: d.data.subject, body_html: d.data.body_html }); setMsg(''); setView('compose'); }
  };

  const saveDraft = async () => {
    if (!form.subject.trim() || !form.body_html.trim()) { setMsg('Betreff und Inhalt ausfüllen.'); return; }
    setSaving(true); setMsg('');
    try {
      const url    = editCamp ? `/api/newsletter/campaigns/${editCamp.id}` : '/api/newsletter/campaigns';
      const method = editCamp ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: auth(), body: JSON.stringify(form) });
      const d = await r.json();
      if (d.success) { setMsg('✅ Entwurf gespeichert.'); load(); if (!editCamp) setView('overview'); }
      else setMsg('❌ ' + d.error);
    } catch { setMsg('❌ Verbindungsfehler'); }
    finally { setSaving(false); }
  };

  const deleteCamp = async (id) => {
    if (!window.confirm('Kampagne löschen?')) return;
    await fetch(`/api/newsletter/campaigns/${id}`, { method: 'DELETE', headers: auth() });
    load();
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Abonnenten löschen?')) return;
    await fetch(`/api/newsletter/subscribers/${id}`, { method: 'DELETE', headers: auth() });
    load();
  };

  const sendCampaign = async (camp) => {
    setSendConfirm(null);
    const r = await fetch(`/api/newsletter/campaigns/${camp.id}/send`, { method: 'POST', headers: auth() });
    const d = await r.json();
    alert(d.success ? `✅ ${d.message}` : `❌ ${d.error}`);
    load();
  };

  const preview = (id) => window.open(`/api/newsletter/campaigns/${id}/preview`, '_blank');

  const STATUS_BADGE = { draft: 'nl-badge--draft', sending: 'nl-badge--sending', sent: 'nl-badge--sent' };
  const STATUS_LABEL = { draft: 'Entwurf', sending: 'Wird gesendet…', sent: 'Versendet' };
  const SUB_BADGE    = { pending: 'nl-badge--draft', active: 'nl-badge--sent', unsubscribed: 'nl-badge--unsub' };
  const SUB_LABEL    = { pending: 'Ausstehend', active: 'Aktiv', unsubscribed: 'Abgemeldet' };

  if (loading) return <div className="nl-loading"><div className="nl-spinner" /><span>Lade Newsletter-Daten…</span></div>;

  return (
    <div className="nl-wrap">
      {/* KPI */}
      <div className="nl-kpi-row">
        <div className="nl-kpi"><div className="nl-kpi-val">{stats.active}</div><div className="nl-kpi-label">Aktive Abonnenten</div></div>
        <div className="nl-kpi"><div className="nl-kpi-val">{stats.total}</div><div className="nl-kpi-label">Gesamt</div></div>
        <div className="nl-kpi"><div className="nl-kpi-val">{campaigns.filter(c => c.status === 'sent').length}</div><div className="nl-kpi-label">Versendete Kampagnen</div></div>
        <div className="nl-kpi nl-kpi--action">
          <button className="nl-btn nl-btn--primary" onClick={openNew}>+ Neue Kampagne</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="nl-tabs">
        {['overview','subscribers'].map(v => (
          <button key={v} className={`nl-tab${view === v ? ' nl-tab--active' : ''}`} onClick={() => setView(v)}>
            {v === 'overview' ? '📨 Kampagnen' : '👥 Abonnenten'}
          </button>
        ))}
      </div>

      {/* KAMPAGNEN */}
      {view === 'overview' && (
        <div className="nl-section">
          {campaigns.length === 0 ? (
            <div className="nl-empty">Noch keine Kampagnen. <button className="nl-link" onClick={openNew}>Erste Kampagne erstellen →</button></div>
          ) : (
            <table className="nl-table">
              <thead><tr><th>Betreff</th><th>Status</th><th>Empfänger</th><th>Erstellt</th><th>Versendet</th><th></th></tr></thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id}>
                    <td className="nl-subject">{c.subject}</td>
                    <td><span className={`nl-badge ${STATUS_BADGE[c.status]}`}>{STATUS_LABEL[c.status]}</span></td>
                    <td>{c.recipient_count || '—'}</td>
                    <td>{fmtDate(c.created_at)}</td>
                    <td>{c.sent_at ? fmtDate(c.sent_at) : '—'}</td>
                    <td className="nl-actions">
                      <button className="nl-icon-btn" title="Vorschau" onClick={() => preview(c.id)}>👁</button>
                      {c.status === 'draft' && <>
                        <button className="nl-icon-btn" title="Bearbeiten" onClick={() => openEdit(c.id)}>✏️</button>
                        <button className="nl-icon-btn nl-icon-btn--send" title="Versenden" onClick={() => setSendConfirm(c)}>📤</button>
                        <button className="nl-icon-btn nl-icon-btn--del" title="Löschen" onClick={() => deleteCamp(c.id)}>🗑</button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ABONNENTEN */}
      {view === 'subscribers' && (
        <div className="nl-section">
          {subscribers.length === 0 ? (
            <div className="nl-empty">Noch keine Abonnenten.</div>
          ) : (
            <table className="nl-table">
              <thead><tr><th>E-Mail</th><th>Name</th><th>Status</th><th>Angemeldet</th><th></th></tr></thead>
              <tbody>
                {subscribers.map(s => (
                  <tr key={s.id}>
                    <td>{s.email}</td>
                    <td>{s.name || '—'}</td>
                    <td><span className={`nl-badge ${SUB_BADGE[s.status]}`}>{SUB_LABEL[s.status]}</span></td>
                    <td>{s.subscribed_at ? fmtDate(s.subscribed_at) : '—'}</td>
                    <td className="nl-actions">
                      <button className="nl-icon-btn nl-icon-btn--del" title="Löschen" onClick={() => deleteSubscriber(s.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* COMPOSE */}
      {view === 'compose' && (
        <div className="nl-section nl-compose">
          <div className="nl-compose-header">
            <button className="nl-back" onClick={() => setView('overview')}>← Zurück</button>
            <h3>{editCamp ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</h3>
          </div>
          {msg && <div className={`nl-msg ${msg.startsWith('✅') ? 'nl-msg--ok' : 'nl-msg--err'}`}>{msg}</div>}
          <div className="nl-field">
            <label>Betreff</label>
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Newsletter Betreff…" />
          </div>
          <div className="nl-field">
            <label>Inhalt (HTML)</label>
            <div className="nl-hint">Du kannst einfaches HTML verwenden: &lt;h2&gt;, &lt;p&gt;, &lt;a href="…"&gt;, &lt;ul&gt;&lt;li&gt;, &lt;strong&gt;, &lt;img src="…"&gt;</div>
            <textarea
              value={form.body_html}
              onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
              placeholder="<h2>Hallo,</h2><p>Hier kommen deine Neuigkeiten…</p>"
              rows={18}
            />
          </div>
          <div className="nl-compose-actions">
            <button className="nl-btn nl-btn--ghost" onClick={() => setView('overview')}>Abbrechen</button>
            <button className="nl-btn nl-btn--secondary" disabled={saving} onClick={saveDraft}>
              {saving ? 'Speichern…' : '💾 Als Entwurf speichern'}
            </button>
            {editCamp && editCamp.status === 'draft' && (
              <button className="nl-btn nl-btn--primary" onClick={() => { saveDraft(); setSendConfirm(editCamp); }}>
                📤 Speichern & Versenden
              </button>
            )}
          </div>
        </div>
      )}

      {/* Send Confirm Modal */}
      {sendConfirm && (
        <div className="nl-modal-backdrop" onClick={() => setSendConfirm(null)}>
          <div className="nl-modal" onClick={e => e.stopPropagation()}>
            <h3>Newsletter versenden?</h3>
            <p>„<strong>{sendConfirm.subject}</strong>" wird an alle <strong>{stats.active} aktiven Abonnenten</strong> gesendet.</p>
            <p className="nl-modal-warning">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="nl-modal-actions">
              <button className="nl-btn nl-btn--ghost" onClick={() => setSendConfirm(null)}>Abbrechen</button>
              <button className="nl-btn nl-btn--primary" onClick={() => sendCampaign(sendConfirm)}>📤 Jetzt versenden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(dt) {
  return new Date(dt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
